import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useGet from "../../../../services/hooks/useGet";
import { apiEndpoints } from "../../../../shared/constants/api-constant";
import { AlertType } from "../../../../shared/constants/enums";
import { Button, Heading } from "../../../../shared/ReusableComponents";
import CloseBtn from "../../../../shared/ReusableComponents/CloseBtn";
import { NewOffsetInput } from "../../../../shared/ReusableComponents/NewOffsetInput";
import { CommonFunctions } from "../../../../shared/utils/CommonFunctions";
import { hideLoader, showLoader } from "../../../../store/currentPreferencesSlice";
import { toast } from "../../../../store/toastSlice";
// import IconButton from "../../../shared/ReusableComponents/IconButton";

interface IFileUploadDailogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, isEdit: boolean, offset: number) => void;
  fileToEdit?: any | null;
}

export const UploadFiles: React.FC<IFileUploadDailogProps> = ({
  open,
  onClose,
  onUpload,
  fileToEdit,
}) => {
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState<number | undefined>(undefined);
  const [offsetError, setOffsetError] = useState("");
  const [fileError, setFileError] = useState(false);
  const dispatch = useDispatch();
  const { data, getService, loading } = useGet();
  const offsetErrorText = "Please enter valid input.";
  const validateFile = (file: File) => {
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const maxSize = 5 * 1024 * 1024; //5mb

    if (file.name.length >= 150) {
      message("File name is too long. It must not exceed 150 characters");
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      message("Invalid File format. Please upload JPG or PDF or PNG.");
      return false;
    }
    if (file.size > maxSize) {
      message(
        "File size exceeds the allowed limit. Please upload file smaller than 5 MB."
      );
      return false;
    }
    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (file) {
      event.target.value = "";
      if (validateFile(file)) {
        setSelectedFile(file);
      }
      setFileError(false);
    } else {
      setFileError(true);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleDragover = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(!isDragging);
  };

  useEffect(() => {
    if (fileToEdit?.id) {
      dispatch(showLoader());
      getService(`${apiEndpoints.TEMPLATEINVESTIGATION}/${fileToEdit.id}`);
    }
    if (fileToEdit) {
      setOffset(fileToEdit.offset === 0 ? undefined : fileToEdit.offset);
    } else {
      setSelectedFile(null);
      setOffset(undefined);
    }
    setFileError(false);
    setOffsetError("");
  }, [fileToEdit, open]);

  useEffect(() => {
    if (data && data) {
      setSelectedFile(data);
    }
  }, [data]);

  useEffect(() => {
    !loading && dispatch(hideLoader());
  }, [loading]);

  const handleUpload = () => {
    setFileError(false);
    setOffsetError("");
    if (!selectedFile) {
      setFileError(true);
      return;
    }
    if (offset === 0) {
      setOffsetError(offsetErrorText);
      return;
    }

    const isEditing = !!fileToEdit;
    const isOffsetChanged = isEditing && fileToEdit?.offset !== offset;
    const isFileChanged = isEditing && selectedFile?.id !== fileToEdit?.id;
    if (isEditing) {
      if (isFileChanged || isOffsetChanged) {
        onUpload(selectedFile, isEditing, offset ?? 0);
      } else {
        message("No changes");
      }
    } else {
      if (selectedFile) {
        onUpload(selectedFile, isEditing, offset ?? 0);
      } else {
        message("File or offset are empty");
      }
    }

    handleClose();
  };

  const handleClose = () => {
    if (!fileToEdit) {
      setSelectedFile(null);
      setOffset(undefined);
    }
    setFileError(false);
    onClose();
  };

  const message = (message: string) => {
    dispatch(
      toast({
        isOpen: true,
        message: message,
        type: AlertType.ERROR,
      })
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "8px",
        },
        zIndex: 999,
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Heading text="Upload" font="20px" isBold={true} />
        <CloseBtn onClose={onClose} />
      </DialogTitle>
      <Divider sx={{ border: "2px solid #B1B1B1", margin: "0px 25px" }} />
      <DialogContent>
        <Heading
          text="Upload your supporting documents one by one in the specified file format"
          font="20px"
          style={{ fontWeight: 500 }}
          isBold
        />
        <div
          style={{
            marginTop: "20px",
            marginBottom: "40px",
            display: "inline-block",
            borderColor: "blue",
            maxWidth: "328px",
          }}
        >
          <NewOffsetInput
            isSingleLineLayout={true}
            onValueChanged={(e) => {
              setOffsetError("");
              setOffset(e);
            }}
            value={offset}
            errorMessage={offsetError}
            onlyHighlightOnError={false}
          />
        </div>

        <Box
          sx={{
            border: fileError
              ? "1px dashed var(--color-error-main)"
              : "1px dashed var(--color-info-main)",
            borderRadius: "8px",
            padding: "20px 60px",
            textAlign: "center",
            cursor: "pointer",
          }}
          onClick={() => document.getElementById("file-input")?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragover}
          onDragLeave={handleDragLeave}
        >
          <span className="material-symbols-outlined upload-icon">
            upload_file
          </span>

          <Heading
            text="Click to upload your file or drag it here."
            classes="upload-text"
            font="16px"
          />
          <Heading
            text="Supported File Formats: PDF, JPG, PNG (Max size: 5MB per file)"
            font="12px"
            isBold={true}
          />

          <input
            id="file-input"
            type="file"
            accept=".pdf, .jpg, .jpeg, .png"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </Box>
        {selectedFile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Tooltip title={selectedFile?.name || fileToEdit?.fileName}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#0288D1",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px" }}
                >
                  attachment
                </span>
                <Heading
                  text={CommonFunctions.truncateText(
                    selectedFile?.name || fileToEdit?.fileName,
                    50
                  )}
                  font="14px"
                />
              </Box>
            </Tooltip>
            <IconButton onClick={() => setSelectedFile(null)}>
              <span
                className="material-symbols-outlined"
                style={{ color: "#000000", fontSize: "20px" }}
              >
                close
              </span>
            </IconButton>
          </Box>
        )}
        {fileError && (
          <Typography color="error">All the fields are required.</Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          m: "35px 17px 22px 0px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Button
          text="Cancel"
          fullWidth={false}
          variant="outlined"
          type="button"
          onClick={handleClose}
        />
        <Button
          text={fileToEdit ? "Update" : "Upload"}
          fullWidth={false}
          className="ml-1"
          onClick={handleUpload}
        />
      </DialogActions>
    </Dialog>
  );
};
